import Vue from 'utils/vueUtils';
import Inboxes from '../collections/Inboxes';
import Inbox from '../models/Inbox';
import Phone from '../models/Phone';
import { TYPES_INBOX } from './user';
import GroupInbox from '../models/GroupInbox';
import getInboxType from '../../utils/getInboxType';
import { baseApi } from 'core/api';
import { sortBy as sortby, findIndex, uniqBy } from 'lodash';
import {
    useMembersStore,
    useOrganizationStore,
    useToastStore,
    useUnreadStore,
    useUserStore,
} from 'store/modules';
import { reactive } from 'vue';

export const defaultInbox = () => ({
    id: 0,
    organization_id: 0,
    defaultAssignedUser: null,
    name: '',
    usersCount: 0,
    memberIds: [],
    createdAt: '',
    unread: 0,
    number: {
        id: 0,
        country: '',
        number: '',
        formattedNumber: '',
    },
    numbers: [],
    owner: {
        id: 0,
        fullName: '',
        firstName: '',
        lastName: '',
        email: '',
        photoUrl: '',
    },
    voicemail: null,
    ivr: null,
    inbox_identifier: null,
    followup: null,
    call_via_aircall: null,
});

export const unifiedInbox = () => ({
    ...defaultInbox(),
    id: null,
});

export const formatOwner = (owner) => ({
    id: owner.id,
    fullName: owner.full_name,
    firstName: owner.first_name,
    lastName: owner.last_name,
    email: owner.email,
    photoUrl: owner.photo_url,
});

export const formatInbox = (inbox) => ({
    id: inbox.id,
    organization_id: inbox.organization_id,
    defaultAssignedUserId: inbox.default_assigned_user_id
        ? inbox.default_assigned_user_id
        : inbox.defaultAssignedUserId,
    isRoundRobin: inbox.is_round_robin ? inbox.is_round_robin : Boolean(inbox.isRoundRobin),
    isHubspotAssignment: inbox.is_hubspot_assignment
        ? inbox.is_hubspot_assignment
        : Boolean(inbox.isHubspotAssignment),
    businessHours: inbox.business_hours ? inbox.business_hours : inbox.businessHours,
    name: inbox.name,
    usersCount: inbox.users_count ? inbox.users_count : inbox.usersCount,
    memberIds: inbox.member_ids ? inbox.member_ids : inbox.memberIds,
    createdAt: inbox.created_at ? inbox.created_at : inbox.createdAt,
    unread: inbox.unread,
    number: inbox.number,
    numbers: Array.isArray(inbox.numbers) ? inbox.numbers.map((item) => item) : [],
    numbersCount: inbox.numbers_count,
    owner: formatOwner(inbox.owner),
    disabled: inbox.disabled,
    voicemail: inbox.voicemail,
    ivr: inbox.ivr,
    is_geomatch: inbox.is_geomatch,
    is_smart: inbox.is_smart,
    is_rcs: inbox.is_rcs,
    is_favorite: inbox.is_favorite,
    is_muted: inbox.is_muted,
    type: inbox.type,
    inbox_identifier: inbox.inbox_identifier,
    color_coding_enabled: inbox.color_coding_enabled,
    followup: inbox.followup,
    call_via_aircall: inbox.call_via_aircall,
    use_conversation_history: Boolean(inbox.use_conversation_history),
    use_global_conversation_history: Boolean(inbox.use_global_conversation_history),
    deprioritized_numbers: inbox.deprioritized_numbers,
    reply_automation: inbox.reply_automation,
    calling_agent_setting: inbox.calling_agent_setting,
});

const state = () => ({
    _unifiedInboxState: null,
    _currentInboxId: null,
    _currentInboxTypeId: null,
    _currentInboxType: null,
    _inbox: defaultInbox(),
    _inbox_prev: defaultInbox(),
    _inboxes: {},
    _inboxesLoading: false,
    _inboxesSettings: {},
    _inboxesGroups: {},
    _inboxesGroupsOrder: [],
    _inboxesFull: {},
    _inboxesAll: {},
    _inboxesAllFormatted: {},
    _inboxesAllLoaded: false,
    _showModalSelectInbox: false,
    _modalSelectInboxData: null,
    _loadingConversation: false,
    _parameters: {
        sortOrder: 'asc',
    },
    _parametersGroups: {
        sortBy: null,
        sortOrder: null,
    },

    _callSettingLoading: false,
    _callSettingDistribution: null,
    _callSettingQueue: null,
    _callSettingQueueAudio: {
        'queue-music': [],
        'queue-message': [],
    },
    _colorCodingLoading: false,
    _conversationHistoryLoading: false,
    _replyAutomationLoading: false,
});

const getters = {
    unifiedInboxState: (state) => state._unifiedInboxState,
    currentInboxId: (state) => state._currentInboxId,
    currentInboxTypeId: (state) => state._currentInboxTypeId,
    currentInboxType: (state) => state._currentInboxType,
    inbox: (state) => state._inbox,
    inboxesLoading: (state) => state._inboxesLoading,
    inboxPrev: (state) => state._inbox_prev,
    inboxes: (state) => state._inboxes,
    inboxesArray: (state) => {
        const array = [];

        for (let key in state._inboxes) {
            array.push(state._inboxes[key]);
        }

        return array;
    },
    inboxesAll: (state) => state._inboxesAll,
    inboxesAllLoaded: (state) => state._inboxesAllLoaded,
    inboxesAllFormatted: (state) => state._inboxesAllFormatted,
    inboxesNumbers: (state) => {
        const array = [];

        for (let key in state._inboxes) {
            const inbox = state._inboxes[key];
            const number = {
                ...inbox.number,
                inboxId: inbox.id,
            };
            const numbers = inbox.numbers.map((item) => ({
                ...item,
                inboxId: inbox.id,
            }));

            array.push(number, ...numbers);
        }

        return uniqBy(array, 'id');
    },
    inboxesSettings: (state) => state._inboxesSettings,
    inboxesSettingsArray: (state) => {
        const array = [];

        for (let key in state._inboxesSettings) {
            array.push(state._inboxesSettings[key]);
        }

        return array;
    },
    inboxesGroups: (state) => state._inboxesGroups,
    inboxesGroupsOrder: (state) => state._inboxesGroupsOrder,
    inboxesGroupsArray: (state) => {
        const array = [];

        for (let key in state._inboxesGroups) {
            array.push(state._inboxesGroups[key]);
        }

        return array;
    },
    showModalSelectInbox: (state) => state._showModalSelectInbox,
    modalSelectInboxData: (state) => state._modalSelectInboxData,
    loadingConversation: (state) => state._loadingConversation,
    inboxParameters: (state) => state._parameters,
    inboxesGroupParameters: (state) => state._parametersGroups,
    callSettingLoading: (state) => state._callSettingLoading,
    callSettingDistribution: (state) => state._callSettingDistribution,
    callSettingQueue: (state) => state._callSettingQueue,
    callSettingQueueAudio: (state) => state._callSettingQueueAudio,
    callSettingQueueAudioQueueMusic: (state) => state._callSettingQueueAudio['queue-music'],
    callSettingQueueAudioQueueMessage: (state) => state._callSettingQueueAudio['queue-message'],
    colorCodingLoading: (state) => state._colorCodingLoading,
    replyAutomationLoading: (state) => state._replyAutomationLoading,
    conversationHistoryLoading: (state) => state._conversationHistoryLoading,
};

const actions = {
    activateInbox(id) {
        this._setInboxActivated({
            id: id,
            type: getInboxType(id),
        });
    },

    updateInboxFavoriteState(data) {
        this._setInboxFavoriteStatusUpdated(data);
    },

    updateInboxMutedState(data) {
        this._setInboxMutedStatusUpdated(data);
    },

    updateInboxes(data) {
        this._setInboxesManualUpdate(data);
    },

    createTeams(form) {
        return form.post('core/teams').then((response) => {
            this._setInboxCreated(response.data);

            return new Inbox(response.data);
        });
    },

    inboxCreated(inbox) {
        this._setInboxCreated(inbox);
    },

    inboxUpdated(inbox) {
        this._setInboxUpdated(inbox);
    },

    inboxBusinessHoursUpdated({ inbox, businessHours }) {
        this._setInboxBusinessHoursUpdated({ inbox, businessHours });
    },

    inboxDeleted(inboxId) {
        this._setInboxDeleted(inboxId);
    },

    inboxMembersUpdated(data) {
        this._setInboxMembersUpdated(data);
    },

    inboxOwnerChanged(data) {
        this._setInboxOwnerChanged(data);
    },

    inboxSingleItem() {
        this._setInboxSingleItem();
    },

    getTeams({ updateCollection = true, origin = false } = {}) {
        return baseApi.get('core/teams/channels').then((response) => {
            if (origin) {
                this._setInboxesAllLoaded(response.data);
            }

            if (updateCollection) {
                this._setInboxesSettingLoaded(response.data);
            }

            return new Inboxes(response.data.map((inbox) => formatInbox(inbox))).models;
        });
    },

    getAssignmentMembers({ inboxId }) {
        return baseApi
            .get(`core/teams/${inboxId}/members?limit=1000&withoutRole=view_only`)
            .then((response) => {
                return response;
            });
    },

    getAICallingAgentSettings({ inboxId }) {
        return baseApi.get(`core/voice/ai-calling-agent/${inboxId}`).then((response) => {
            return response;
        });
    },

    getAICallingAgents() {
        return baseApi
            .get('calling-agent/auth/api/v1/calling-agents?offset=0&limit=1000')
            .then((response) => {
                return response;
            });
    },

    updateAICallingAgentSettings({ inboxId, data }) {
        return baseApi.post(`core/voice/ai-calling-agent/${inboxId}`, data)
        .then((response) => {
            this._setInboxUpdateSettingsAICallingAgent({
                id: inboxId,
                data: response.data,
            });

            useToastStore().showToast({
                type: 'success',
                message: 'AI Calling Agent settings updated successfully',
            });

            return response;
        })
        .catch((e) => {
            if (e?.response?.status === 422) {
                useToastStore().showToast({
                    type: 'danger',
                    message: e?.response?.data?.message || e?.response?.data?.is_active[0] || 'AI Calling Agent settings update failed',
                });

                return e?.response;
            }

            useToastStore().showToast({
                type: 'danger',
                message: 'AI Calling Agent settings update failed',
            });

            throw e;
        });
    },

    getHubspotFields({ inboxId }) {
        return baseApi
            .get(`core/teams/${inboxId}/assigment-settings/hubspot-fields?cache_ignore=1`)
            .then((response) => {
                return response;
            });
    },

    setRoundRobinAssignment({ inboxId }) {
        return baseApi.put(`core/teams/${inboxId}/round-robin`).then((response) => {
            return response;
        });
    },

    getAssignmentSettings({ inboxId }) {
        return baseApi.get(`core/teams/${inboxId}/assigment-settings`).then((response) => {
            return response.data;
        });
    },

    updateAssignmentSettings({ inboxId, settings }) {
        return baseApi
            .post(`core/teams/${inboxId}/assigment-settings`, settings)
            .then((response) => {
                const data = response.data;

                this._setInboxUpdateProps({
                    id: inboxId,
                    props: {
                        defaultAssignedUserId: data.manual.default_user_id,
                    },
                });

                return data;
            });
    },

    updateCallingMembers({ inboxId, members }) {
        return baseApi
            .post(`core/voice/settings/members/${inboxId}`, { members })
            .then((response) => {
                return response;
            });
    },

    updateTeamsByIdDefaultAssignee({ assignee, inbox }) {
        const inboxId = inbox.id;
        const userId = assignee.id || 0;
        const oldUserId = inbox.defaultAssignedUserId;

        this._setInboxDefaultAssigneeSet({
            inboxId,
            userId,
        });

        return baseApi
            .put(`core/teams/${inboxId}/default-assignee`, { user_id: userId })
            .catch((error) => {
                this._setInboxDefaultAssigneeSetError({
                    inboxId,
                    oldUserId,
                });
                return error;
            });
    },

    updateTeamsByIdRoundRobin({ inboxId, form }) {
        return form.put(`core/teams/${inboxId}/round-robin`).then((response) => {
            this._setInboxRoundRobinUpdated({
                inboxId: inboxId,
                isRoundRobin: response.data.is_round_robin,
            });

            return response;
        });
    },

    updateTeamsByIdHubspot({ inboxId, form }) {
        return form.put(`core/teams/${inboxId}/hubspot`).then((response) => {
            this._setInboxHubspotAssignmentUpdated({
                inboxId: inboxId,
                isHubspotAssignment: response.data.is_hubspot_assignment,
            });
            return response;
        });
    },

    updateTeamsByIdCallSettings({ id, callSettings }) {
        return baseApi.put(`core/teams/${id}/call-settings`, callSettings).then(({ data }) => {
            this._setInboxUpdated(data);

            return data;
        });
    },

    inboxUpdateCount(inbox) {
        let count = 0;

        this._setInboxCountUpdate(inbox);

        for (let key in this._inboxes) {
            count += this._inboxes[key].unread;
        }

        return count;
    },

    changeStatusModalSelectInbox(data) {
        this._setInboxChangeStatusModal(data);
    },

    changeLoadingStatusModalSelectInbox(status) {
        this._setInboxStatusLoadingModal(status);
    },

    updateVoiceVoicemailSettingsById({ form, id }) {
        return form
            .post(`core/voice/voicemail/settings/${id}`)
            .then((response) => {
                this._setInboxUpdateSettingsVoiceMail({
                    id: id,
                    data: response.data,
                });

                return response;
            })
            .catch((e) => {
                if (e?.response?.status === 422) {
                    useToastStore().showToast({
                        type: 'danger',
                        message: e?.response?.data?.is_active[0],
                    });
                }

                return e?.response;
            });
    },

    updateVoiceIVRSettingsById({ id, data }) {
        return baseApi.post(`core/voice/ivr/settings/${id}`, data).then((response) => {
            this._setInboxUpdateSettingsIvr({
                id: id,
                data: response.data,
            });

            return response;
        });
    },

    createVoiceVoicemailGreetingUploadById({ formData, cancelToken, id }) {
        return baseApi
            .post(`core/voice/voicemail/greeting/upload/${id}`, formData, {
                cancelToken: cancelToken,
            })
            .then((response) => {
                this._setInboxUpdateMediaVoiceMail({
                    media: response.data,
                    is_make_active: formData.get('is_make_active'),
                    type: formData.get('type'),
                    id: id,
                });

                return response;
            })
            .catch((error) => {
                useToastStore().showToast({
                    type: 'danger',
                    message: error.message,
                });
            });
    },

    createVoiceIVRGreetingUploadById({ formData, cancelToken, id }) {
        return baseApi
            .post(`core/voice/ivr/greeting/${id}`, formData, {
                cancelToken: cancelToken,
            })
            .then((response) => {
                this._setInboxUpdateMediaIvr({
                    inboxId: id,
                    media: response.data,
                });

                return response;
            })
            .catch((error) => {
                useToastStore().showToast({
                    type: 'danger',
                    message: error.message,
                });
            });
    },

    updateVoiceVoicemailGreetingUploadById({ form, id }) {
        return form.put(`core/voice/voicemail/greeting/update/${id}`).then((response) => {
            this._setInboxUpdateMediaVoiceMail({
                media: response.data,
            });

            return response;
        });
    },

    updateVoiceIVRGreetingUploadById({ form, id, inboxId }) {
        return form.put(`core/voice/ivr/greeting/${id}`).then((response) => {
            this._setInboxUpdateMediaIvr({
                inboxId,
                media: response.data,
            });

            return response;
        });
    },

    deleteVoiceVoicemailGreetingDeleteById({ idGreeting, inboxId, type }) {
        return baseApi
            .delete(`core/voice/voicemail/greeting/delete/${idGreeting}`)
            .then((response) => {
                this._setInboxDeleteMediaVoiceMail({
                    idGreeting: idGreeting,
                    inboxId: inboxId,
                    type: type,
                });

                return response;
            });
    },

    deleteVoiceIVRGreetingDeleteById({ idGreeting, inboxId }) {
        return baseApi.delete(`core/voice/ivr/greeting/${idGreeting}`).then((response) => {
            this._setInboxDeleteMediaIvr({
                idGreeting: idGreeting,
                inboxId: inboxId,
            });

            return response;
        });
    },

    getVoiceVoicemailPreview({ message = '', form }) {
        if (form) {
            return form
                .get('core/voice/voicemail/greeting/synthesize', {
                    responseType: 'blob',
                })
                .then((response) => {
                    return response.data;
                });
        }

        const options = {
            params: {
                message: message,
            },
            responseType: 'blob',
        };

        return baseApi.get('core/voice/voicemail/greeting/synthesize', options).then((response) => {
            return response.data;
        });
    },

    updateTeamsByIdNumbers({ id, form }) {
        return form.post(`core/teams/${id}/numbers`).then((response) => {
            return response.data;
        });
    },

    updateNumbersUnassignedCreate(form) {
        return form.post('core/numbers/unassigned/create', form).then((response) => {
            return new Phone(response.data);
        });
    },

    updateTeamsByIdGeoMatch(id) {
        return baseApi.put(`core/teams/${id}/geo-match`).then((response) => {
            const data = response.data;

            this._setInboxUpdated(data);

            return data;
        });
    },

    updateNumbersByIdUnassignFromInboxModalRemove({ numberId, form }) {
        return form.post(`core/numbers/${numberId}/unassign-from-inbox`).then((response) => {
            return response.data;
        });
    },

    updateTeamsByIdMakeDefault(id) {
        return baseApi.post(`core/teams/${id}/make-default`).then((response) => {
            useUserStore()._setInboxMakeDefaultOwner({
                inboxId: id,
                data: response.data,
            });

            return response.data;
        });
    },

    updateTeamsByIdMakeDefaultForById({ inboxId, memberId }) {
        return baseApi
            .post(`core/teams/${inboxId}/make-default-for/${memberId}`)
            .then((response) => {
                this._setInboxMakeDefault({
                    inboxId: inboxId,
                    memberId: memberId,
                    data: response.data,
                });

                return response.data;
            });
    },

    deleteTeamsGroupsByIdRemoveTeamById({ inboxGroupId, inboxId }) {
        return baseApi
            .delete(`core/teams/groups/${inboxGroupId}/remove-team/${inboxId}`)
            .then((response) => {
                return response.data;
            });
    },

    deleteTeamsGroupsById({ inboxGroupId }) {
        return baseApi.delete(`core/teams/groups/${inboxGroupId}`).then((response) => {
            return response.data;
        });
    },

    deleteTeamsByIdMembersById({ form, inboxId, memberId }) {
        return form.delete(`core/teams/${inboxId}/members/${memberId}`).then((response) => {
            return response.data;
        });
    },

    updateTeamsByIdChangeOwner({ form, inboxId }) {
        return form.put(`core/teams/${inboxId}/change-owner`).then((response) => {
            return response.data;
        });
    },

    getTeamsByIdNumbers({ inboxId }) {
        return baseApi.get(`core/teams/${inboxId}/numbers`).then((response) => {
            return response.data;
        });
    },

    importHubspotTeams() {
        return baseApi.get('core/groups/import-integration-teams').then((response) => {
            return response.data;
        });
    },

    getTeamsByIdDefaultAssignee({ inboxId }) {
        return baseApi.get(`core/teams/${inboxId}/default-assignee`).then((response) => {
            return response.data;
        });
    },

    updateTeamsGroupsByIdAddTeams({ form, inboxGroupId }) {
        return form.post(`core/teams/groups/${inboxGroupId}/add-teams`).then((response) => {
            return response.data;
        });
    },

    updateTeamsGroupsById({ form, inboxGroupId, data, useAxios = false }) {
        if (useAxios) {
            return baseApi.post(`core/teams/groups/${inboxGroupId}`, data).then((response) => {
                return response.data;
            });
        }

        return form.post(`core/teams/groups/${inboxGroupId}`).then((response) => {
            return response.data;
        });
    },

    updateTeamsGroupsCreate({ form }) {
        return form.post('core/teams/groups/create').then((response) => {
            return response.data;
        });
    },

    updateTeamsUnassigned({ form }) {
        return form.post('core/teams/unassigned').then((response) => {
            return response.data;
        });
    },

    updateTeamsById({ form, inboxId }) {
        return form.post(`core/teams/${inboxId}`).then((response) => {
            return response.data;
        });
    },

    updateTeamsColorCodingById({ color_coding_enabled, inboxId }) {
        this._setColorCodingLoading();

        return baseApi
            .put(`core/teams/${inboxId}/color-coding`, { enabled: color_coding_enabled })
            .then((response) => {
                useToastStore().showToast({
                    type: 'info',
                    message: 'Color Coding setting has been updated',
                });
                this.inboxUpdated(response.data);
            })
            .finally(() => {
                this._setColorCodingLoaded();
            });
    },

    updateTeamsReplyAutomationById({ params, inboxId }) {
        this._setReplyAutomationLoading(true);

        return baseApi
            .patch(`core/teams/${inboxId}`, params)
            .then((response) => {
                useToastStore().showToast({
                    type: 'success',
                    message: 'AI automation saved!',
                });
                this.inboxUpdated(response.data);
            })
            .finally(() => {
                this._setReplyAutomationLoading(false);
            });
    },

    updateTeamsConversationHistoryById({
        use_global_conversation_history,
        use_conversation_history,
        inboxId,
    }) {
        this._setConversationsHistoryLoading(true);

        return baseApi
            .post(`core/teams/${inboxId}/conversation-history-settings`, {
                use_conversation_history,
                use_global_conversation_history,
            })
            .then((response) => {
                useToastStore().showToast({
                    type: 'info',
                    message: 'Conversation history setting has been updated',
                });
                this.inboxUpdated(response.data);
            })
            .finally(() => {
                this._setConversationsHistoryLoading(false);
            });
    },

    updateDeprioritizedNumbers({ numberIds, inboxId }) {
        return baseApi
            .post(`core/teams/${inboxId}/numbers/deprioritize`, {
                deprioritized_numbers: numberIds,
            })
            .then((response) => {
                this.inboxUpdated(response.data);
            });
    },

    deleteTeamsById({ form, inboxId }) {
        return form.delete(`core/teams/${inboxId}`).then((response) => {
            return response.data;
        });
    },

    handleInboxUpdateProps({ id, props }) {
        this._setInboxUpdateProps({
            id: id,
            props: props,
        });
    },

    handleAddToFavorite(inbox) {
        return baseApi
            .post('core/teams/favorite', {
                inbox_id: inbox.id,
                inbox_type: inbox.type ? inbox.type : 'inbox_group',
                favorite: inbox.is_favorite ? 0 : 1,
            })
            .then(() => {
                if (inbox.id === 0) {
                    this.updateUnifiedInboxState({
                        id: 0,
                        is_favorite: !inbox.is_favorite,
                        type: 'unified-inbox',
                    });
                } else if (inbox.type === 'inbox') {
                    this.updateInboxFavoriteState({
                        id: inbox.id,
                        is_favorite: !inbox.is_favorite,
                    });
                } else {
                    this.updateGroupFavoriteState({
                        id: inbox.id,
                        is_favorite: !inbox.is_favorite,
                    });
                }
            })
            .catch((error) => {
                useToastStore().showToast({
                    type: 'danger',
                    message: error.message,
                });
            });
    },

    inboxesGroupsUpdateSort({ sortBy, sortOrder }) {
        const inboxesSettingsArray = this.inboxesSettingsArray;

        let inboxesGroups = sortby(this._inboxesGroups, (item) => {
            if (sortBy === 'inboxes') {
                const { teams } = item;

                return inboxesSettingsArray.filter(
                    (inbox) => findIndex(teams, (el) => el.id === inbox.id) >= 0
                );
            }

            if (typeof item[sortBy] === 'string') {
                return item[sortBy].toLowerCase();
            }

            return item[sortBy];
        });

        if (sortOrder === 'desc') {
            inboxesGroups = inboxesGroups.reverse();
        }

        this._setInboxesGroupUpdateSort({ inboxesGroups, sortBy, sortOrder });
    },

    inboxUpdateSort({ sortBy, sortOrder }) {
        this._setInboxesUpdateSort({ sortBy, sortOrder });
    },

    updateGroupFavoriteState(data) {
        this._setUpdateGroupFavoriteState(data);
    },

    updateGroupMutedState(data) {
        this._setUpdateGroupMutedState(data);
    },

    updateGroupSettings(data) {
        this._setUpdateGroupSettings(data);
    },

    addInboxInGroup(data) {
        this._setAddInboxInGroup(data);
    },

    addInboxGroup(group) {
        this._setAddInboxesGroup(group);
    },

    inboxGroupUpdate(data) {
        this._setInboxesGroupsUpdate(data);
    },

    inboxGroupDelete(groupId) {
        this._setInboxesGroupDelete(groupId);
    },

    updateUnifiedInboxState(data) {
        this._setUpdateUnifiedInboxState(data);
    },

    getCallSettings({ teamId }) {
        this._setInboxCallSettingsLoading(true);

        return baseApi.get(`core/teams/${teamId}/call-settings`).then((response) => {
            const data = response.data;

            this._setInboxCallSettingsDistribution(data['call-distribution']);
            this._setInboxCallSettingsQueue(data['call-queue']);
            this._setInboxCallSettingsLoading(false);

            return data;
        });
    },

    getCallSettingsAvailableIntegrations() {
        return baseApi.get('core/call/call-settings/available-integrations').then((response) => {
            return response.data;
        });
    },

    updateCallSettings({ form, teamId }) {
        return form
            .post(`core/teams/${teamId}/call-settings/call-distribution`)
            .then((response) => {
                const data = response.data;

                this._setInboxCallSettingsDistribution(data);

                return data;
            });
    },

    handleMuteInboxNotifications(inbox) {
        return baseApi
            .post(`core/teams/${inbox.id}/mute`, {
                is_muted: inbox.is_muted ? 0 : 1,
            })
            .then(() => {
                if (inbox.id === 0) {
                    this.updateUnifiedInboxState({
                        id: 0,
                        is_muted: !inbox.is_muted,
                        type: 'unified-inbox',
                    });
                } else if (inbox.type === 'inbox') {
                    this.updateInboxMutedState({
                        id: inbox.id,
                        is_muted: !inbox.is_muted,
                    });
                } else {
                    this.updateGroupMutedState({
                        id: inbox.id,
                        is_muted: !inbox.is_muted,
                    });
                }
            })
            .catch((error) => {
                useToastStore().showToast({
                    type: 'danger',
                    message: error.message,
                });
            });
    },

    getTeamsAll() {
        return baseApi.get('core/teams/channels/all').then((response) => {
            return new Inboxes(response.data.map((inbox) => formatInbox(inbox))).models;
        });
    },

    updateTeamsByIdCallSettingsCallQueue({ inboxId, form }) {
        return form.post(`core/teams/${inboxId}/call-settings/call-queue`).then((response) => {
            const data = response.data;

            this._setInboxCallSettingsQueue(data);

            return data;
        });
    },

    getCallSettingsAudio({ organizationId }) {
        return baseApi
            .get('core/call-settings/audio', {
                params: {
                    organization: organizationId,
                },
            })
            .then((response) => {
                const data = response.data;

                this._setInboxCallSettingsAudio({
                    data: data,
                });

                return data;
            });
    },

    createTeamsByIdCallSettingsAudio({ inboxId, params = {}, type = '' }) {
        return baseApi
            .post(`core/teams/${inboxId}/call-settings/audio`, params)
            .then((response) => {
                const data = response.data;

                this._setInboxCallSettingsAudio({
                    data: data,
                    type: type,
                });

                return data;
            });
    },

    updateCallSettingsAudioById({ id, type, name }) {
        return baseApi
            .put(`core/call-settings/audio/${id}`, {
                name: name,
            })
            .then((response) => {
                const data = response.data;

                this._setInboxCallSettingsAudio({
                    data: data,
                    type: type,
                });

                return data;
            });
    },

    deleteCallSettingsAudioById({ id, type }) {
        return baseApi.delete(`core/call-settings/audio/${id}`).then(() => {
            this._setInboxCallSettingsAudioDelete({ id, type });
        });
    },

    updateCallSettingsAudioSynthesize({ text }) {
        return baseApi.get('core/call-settings/audio/synthesize', {
            params: {
                message: text,
            },
        });
    },

    resetCallQueueSettings() {
        this._setInboxCallSettingsReset();
    },

    getTeamsById({ id, type = 'init' } = {}) {
        if (!id) return;

        const inbox = this.inboxes[id];

        if (inbox && type === 'init') return inbox;

        return baseApi.get(`core/teams/${id}`).then((response) => {
            const inbox = new Inbox(formatInbox(response.data));

            this._setInboxCreated(inbox);

            return inbox;
        });
    },

    getTeamsPersonal() {
        return baseApi
            .get('core/teams/channels/personal', {
                params: {
                    withUnified: true,
                },
            })
            .then((response) => {
                return response.data;
            });
    },

    getTeamsGroupsPersonal() {
        return baseApi.get('core/teams/groups/personal').then((response) => {
            if (response.data?.data) {
                return response.data?.data;
            }

            return response.data;
        });
    },

    getAllTeams({ isLoading = true } = {}) {
        if (isLoading) {
            if (this.inboxesLoading) return;
            if (this.inboxesArray.length) return;

            this._setInboxesLoading(true);
        }

        return Promise.all([this.getTeamsPersonal(), this.getTeamsGroupsPersonal()]).then(
            ([inboxes, groupInboxes]) => {
                this._setInboxesLoaded({
                    inboxes: inboxes,
                    inboxesGroups: groupInboxes,
                });
                this._setInboxesLoading(false);
            }
        );
    },

    updateTeamsByIdCallViaAircallSettings({ id, status }) {
        return baseApi
            .post(`core/teams/${id}/call-settings/call-via-aircall`, { is_active: status })
            .then(({ data }) => {
                this._setInboxUpdateAircallSettings({ id, data });

                return data;
            });
    },

    _setInboxCreated(inbox) {
        const inboxInstance = inbox instanceof Inbox ? inbox : new Inbox(formatInbox(inbox));

        this._inboxes[inboxInstance.id] = inboxInstance;

        if (this._inboxesSettings) {
            this._inboxesSettings[inboxInstance.id] = inboxInstance;
        }
    },

    _setInboxesLoading(status) {
        this._inboxesLoading = status;
    },

    _setInboxCallSettingsReset() {
        this._callSettingLoading = false;
        this._callSettingDistribution = null;
        this._callSettingQueue = null;
        this._callSettingQueueAudio = {
            'queue-music': [],
            'queue-message': [],
        };
        this._colorCodingLoading = false;
        this._replyAutomationLoading = false;
    },

    _setInboxCallSettingsAudioDelete({ id, type }) {
        this._callSettingQueueAudio[type] = this._callSettingQueueAudio[type].filter((item) => {
            return item.id !== id;
        });
    },

    _setInboxCallSettingsAudio({ data, type }) {
        if (type) {
            const index = this._callSettingQueueAudio[type].findIndex(
                (item) => item.id === data.id
            );

            if (index !== -1) {
                Vue.set(this._callSettingQueueAudio[type], index, data);
            } else {
                this._callSettingQueueAudio[type].push(data);
            }
        } else {
            this._callSettingQueueAudio = {
                'queue-music': data['queue-music'] || [],
                'queue-message': data['queue-message'] || [],
            };
        }
    },

    _setInboxCallSettingsLoading(status) {
        this._callSettingLoading = status;
    },

    _setInboxCallSettingsDistribution(data) {
        this._callSettingDistribution = data;
    },

    _setInboxCallSettingsQueue(data) {
        this._callSettingQueue = data;
    },

    _setUpdateUnifiedInboxState(data) {
        this._unifiedInboxState = data;
    },

    _setUpdateGroupFavoriteState({ id, is_favorite }) {
        if (this._inboxesGroups[id]) {
            this._inboxesGroups[id].update({
                is_favorite: is_favorite,
            });

            Vue.set(this._inboxesGroups, id, this._inboxesGroups[id]);
        }
    },

    _setUpdateGroupMutedState({ id, is_muted }) {
        if (this._inboxesGroups[id]) {
            this._inboxesGroups[id].update({
                is_muted: is_muted,
            });

            Vue.set(this._inboxesGroups, id, this._inboxesGroups[id]);
        }
    },

    _setUpdateGroupSettings({ description, name, id, teams }) {
        if (this._inboxesGroups[id]) {
            this._inboxesGroups[id].update({
                name: name,
                description: description,
                teams: teams,
            });

            Vue.set(this._inboxesGroups, id, this._inboxesGroups[id]);
        }
    },

    _setAddInboxInGroup({ inboxes, groupId }) {
        if (this._inboxesGroups[groupId]) {
            this._inboxesGroups[groupId].update({
                teams: this._inboxesGroups[groupId].teams.concat(inboxes),
            });

            Vue.set(this._inboxesGroups, groupId, this._inboxesGroups[groupId]);
        }
    },

    _setAddInboxesGroup(group) {
        this._inboxesGroupsOrder.push(group.id);

        Vue.set(this._inboxesGroups, group.id, new GroupInbox(group));
    },

    _setInboxesGroupsUpdate({ inboxId, inboxGroupId }) {
        if (this._inboxesGroups[inboxGroupId]) {
            this._inboxesGroups[inboxGroupId].update({
                teams: this._inboxesGroups[inboxGroupId].teams.filter(
                    (inbox) => inbox.id !== inboxId
                ),
            });

            Vue.set(this._inboxesGroups, inboxGroupId, this._inboxesGroups[inboxGroupId]);
        }
    },

    _setInboxesGroupDelete(id) {
        if (this._inboxesGroups[id]) {
            this._inboxesGroupsOrder = this._inboxesGroupsOrder.filter((item) => item !== id);

            delete this._inboxesGroups[id];
        }
    },

    _setInboxesGroupUpdateSort({ inboxesGroups, sortOrder, sortBy }) {
        this._inboxesGroups = inboxesGroups.reduce((o, i) => {
            o[i.id] = new GroupInbox(i);

            return o;
        }, {});
        this._inboxesGroupsOrder = inboxesGroups.map((item) => item.id);
        this._parametersGroups.sortBy = sortBy;
        this._parametersGroups.sortOrder = sortOrder;
    },

    _setInboxesUpdateSort({ sortOrder, sortBy }) {
        this._parametersGroups.sortBy = sortBy;
        this._parametersGroups.sortOrder = sortOrder;
    },

    _setMemberTeamUpdate({ inboxId, members }) {
        if (inboxId) {
            const inboxSettings = this._inboxesSettings[inboxId];
            const inboxMain = this._inboxes[inboxId];

            if (inboxSettings) {
                inboxSettings.memberIds = members.map((item) => item.id);

                Vue.set(this._inboxesSettings, inboxId, inboxSettings);
            }

            if (inboxMain) {
                inboxMain.memberIds = members.map((item) => item.id);

                Vue.set(this._inboxes, inboxId, inboxMain);
            }
        }
    },

    _setInboxUpdateProps({ id, props }) {
        let inboxSettings = this._inboxesSettings[id];
        let inboxMain = this._inboxes[id];

        if (inboxSettings) {
            inboxSettings = inboxSettings.update(props);

            this._inboxesSettings[id] = inboxSettings;
        }

        if (inboxMain) {
            inboxMain = inboxMain.update(props);

            this._inboxes[id] = inboxMain;
        }
    },

    _setInboxUpdateAircallSettings({ id, data }) {
        const inboxSettings = this._inboxesSettings[id];

        if (inboxSettings && data.id) {
            inboxSettings.update({
                ...inboxSettings,
                call_via_aircall: data,
            });
        }
    },

    _setInboxMakeDefault({ data, inboxId, memberId }) {
        const inbox = this._inboxesSettings[inboxId];

        if (inbox) {
            inbox.disabled = data.disabled;

            Vue.set(this._inboxesSettings, inbox.id, inbox);
        }

        useMembersStore()._setInboxMakeDefault({ data, memberId });
    },

    _setInboxFavoriteStatusUpdated({ id, is_favorite }) {
        if (this._inboxes[id]) {
            this._inboxes[id].is_favorite = is_favorite;
        }
    },

    _setInboxMutedStatusUpdated({ id, is_muted }) {
        if (this._inboxes[id]) {
            this._inboxes[id].is_muted = is_muted;
        }
    },

    _setInboxesSettingLoaded(inboxes) {
        inboxes = Array.isArray(inboxes) ? inboxes : [];

        this._inboxesSettings = inboxes.reduce((o, i) => {
            o[i.id] = new Inbox(formatInbox(i));

            return o;
        }, {});
    },

    _setInboxesAllLoaded(inboxes) {
        const inboxesArray = Array.isArray(inboxes) ? inboxes : [];
        this._inboxesAllFormatted = inboxes.reduce((o, i) => {
            if (i.id === 0) return o;

            o[i.id] = new Inbox(formatInbox(i));

            return o;
        }, {});

        this._inboxesAll = inboxesArray;
        this._inboxesAllLoaded = true;
    },

    _setInboxUpdateSettingsVoiceMail({ id, data }) {
        if (this._inboxes[id]) {
            this._inboxes[id].voicemail = {
                ...this._inboxes[id].voicemail,
                ...data,
            };
        }

        if (this._inboxesSettings[id]) {
            this._inboxesSettings[id].voicemail = {
                ...this._inboxesSettings[id].voicemail,
                ...data,
            };
        }
    },

    _setInboxUpdateSettingsAICallingAgent({ id, data }) {
        if (this._inboxes[id]) {
            this._inboxes[id].calling_agent_setting = {
                ...this._inboxes[id].calling_agent_setting,
                ...data,
            };
        }

        if (this._inboxesSettings[id]) {
            this._inboxesSettings[id].calling_agent_setting = {
                ...this._inboxesSettings[id].calling_agent_setting,
                ...data,
            };
        }
    },

    _setInboxUpdateSettingsIvr({ id, data }) {
        if (this._inboxes[id]) {
            this._inboxes[id].ivr = {
                ...this._inboxes[id].ivr,
                ...data,
            };
        }

        if (this._inboxesSettings[id]) {
            this._inboxesSettings[id].ivr = {
                ...this._inboxesSettings[id].ivr,
                ...data,
            };
        }
    },

    _setInboxUpdateMediaVoiceMail({ media, is_make_active, type, id }) {
        if (Number(is_make_active)) {
            const field =
                type === 'open_hours' ? 'open_hours_greeting_id' : 'after_hours_greeting_id';

            if (this._inboxes[id]) {
                this._inboxes[id].voicemail[field] = media.id;
            }

            if (this._inboxesSettings[id]) {
                this._inboxesSettings[id].voicemail[field] = media.id;
            }
        }

        useOrganizationStore()._setInboxUpdateMediaVoiceMail({ media });
    },

    _setInboxUpdateMediaIvr({ inboxId, media }) {
        if (this._inboxes[inboxId]) {
            this._inboxes[inboxId].ivr.organization_ivr_greeting_id = media.id;
        }

        if (this._inboxesSettings[inboxId]) {
            this._inboxesSettings[inboxId].ivr.organization_ivr_greeting_id = media.id;
        }

        useOrganizationStore()._setInboxUpdateMediaIvr({ inboxId, media });
    },

    _setInboxDeleteMediaIvr({ idGreeting, inboxId }) {
        if (this._inboxes[inboxId]) {
            if (this._inboxes[inboxId].ivr.organization_ivr_greeting_id === idGreeting) {
                this._inboxes[inboxId].ivr.organization_ivr_greeting_id = 0;
            }
        }

        if (this._inboxesSettings[inboxId]) {
            if (this._inboxesSettings[inboxId].ivr.organization_ivr_greeting_id === idGreeting) {
                this._inboxesSettings[inboxId].ivr.organization_ivr_greeting_id = 0;
            }
        }

        useOrganizationStore()._setInboxDeleteMediaIvr({ idGreeting });
    },

    _setInboxDeleteMediaVoiceMail({ idGreeting, inboxId, type }) {
        const field = type === 'open_hours' ? 'open_hours_greeting_id' : 'after_hours_greeting_id';

        if (this._inboxes[inboxId]) {
            if (this._inboxes[inboxId].voicemail[field] === idGreeting) {
                this._inboxes[inboxId].voicemail[field] = 0;
            }
        }

        if (this._inboxesSettings[inboxId]) {
            if (this._inboxesSettings[inboxId].voicemail[field] === idGreeting) {
                this._inboxesSettings[inboxId].voicemail[field] = 0;
            }
        }

        useOrganizationStore()._setInboxDeleteMediaVoiceMail({ idGreeting });
    },

    _setUpdateInboxId(data) {
        this._currentInboxId = data.currentInboxId;
        this._currentInboxTypeId = data.currentInboxTypeId;
        this._currentInboxType = data.currentInboxType;

        if (TYPES_INBOX.GROUP === data.currentInboxType) {
            this._inbox = this._inboxesGroups[data.currentInboxTypeId];
        } else {
            this._inbox =
                data.currentInboxId === 0
                    ? new Inbox(unifiedInbox())
                    : this._inboxes[data.currentInboxId];
        }
    },

    _setInboxStatusLoadingModal(status) {
        this._loadingConversation = status;
    },

    _setInboxSingleItem() {
        let inboxArr = Array.from(Object.values(this._inboxes));
        if (inboxArr.length === 1) {
            this._inbox = inboxArr[0];
        }
    },

    _setInboxChangeStatusModal(data) {
        this._showModalSelectInbox = !!data;
        this._modalSelectInboxData = data;
    },

    _setUserOwnerLoaded(user) {
        this._currentInboxId = user.currentInboxId;
        this._currentInboxTypeId = user.currentInboxTypeId;
        this._currentInboxType = user.currentInboxType;
    },

    _setInboxesLoaded({ inboxes, inboxesGroups, unifiedInbox: unifiedInboxState }) {
        this._inboxes = inboxes.reduce((o, i) => {
            if (i.id === 0) return o;

            o[i.id] = new Inbox(formatInbox(i));

            return o;
        }, {});
        this._inboxesGroups = inboxesGroups.reduce((o, i) => {
            o[i.id] = new GroupInbox(i);

            return o;
        }, {});
        this._inboxesGroupsOrder = inboxesGroups.map((item) => item.id);
        this._unifiedInboxState = unifiedInboxState || inboxes.find((item) => item.id === 0);
        this._setInboxesAllLoaded(inboxes);

        if (this._currentInboxType === TYPES_INBOX.GROUP) {
            this._inbox = this._inboxesGroups[this._currentInboxTypeId];
        } else {
            if (this._currentInboxTypeId) {
                let inbox = this._inboxes[this._currentInboxTypeId];

                if (inbox) {
                    this._inbox = inbox;
                    this._inbox_prev = inbox;
                }
            } else {
                if (this._currentInboxType === TYPES_INBOX.UNIFIED) {
                    this._inbox = new Inbox(unifiedInbox());
                    this._inbox_prev = new Inbox(unifiedInbox());
                }
            }
        }

        useUnreadStore()._setInboxesLoaded({ inboxes });
    },

    _setInboxCountUpdate(inbox) {
        const inboxId = inbox.id || inbox.inbox_id;

        if (this._inboxes[inboxId]) {
            this._inboxes[inboxId].unread = inbox.unread;
        }

        if (this._inbox.id === inboxId) {
            this._inbox.unread = inbox.unread;
        }
    },

    _setInboxesManualUpdate({ id, type }) {
        for (let item in this._inboxesSettings) {
            if (this._inboxes[item]) {
                this._inboxes[item] = this._inboxesSettings[item];
            }
        }

        if (id && type) {
            this._inbox = type === TYPES_INBOX.GROUP ? this._inboxesGroups[id] : this._inboxes[id];
        }
    },

    _setInboxActivated({ id, type }) {
        this._inbox_prev = this._inbox;

        if (type === TYPES_INBOX.GROUP) {
            this._inbox = this._inboxesGroups[id];
        } else {
            this._inbox =
                this._inboxes[id] || new Inbox(id === null ? unifiedInbox() : defaultInbox());
        }
    },

    _setInboxUpdated(item) {
        if (this._inbox.id === item.id) {
            this._inbox.update(item);
        }

        const inbox = this._inboxes[item.id];
        const inboxSettings = this._inboxesSettings[item.id];

        if (inbox) {
            inbox.update(item);
        }

        if (inboxSettings) {
            inboxSettings.update(item);
        }
    },

    _setInboxBusinessHoursUpdated({ inbox, businessHours }) {
        const inboxId = inbox.id;

        if (this._inboxes && this._inboxes[inboxId]) {
            const inbox = this._inboxes[inboxId];

            Object.assign(inbox['businessHours'], businessHours);
        }

        if (this._inboxesSettings && this._inboxesSettings[inboxId]) {
            const inbox = this._inboxesSettings[inboxId];

            Object.assign(inbox['businessHours'], businessHours);
        }
    },

    _setInboxDeleted(inboxId) {
        Vue.delete(this._inboxes, inboxId);

        if (this._inboxesSettings && this._inboxesSettings[inboxId]) {
            Vue.delete(this._inboxesSettings, inboxId);
        }

        if (this._inbox.id === inboxId) {
            this._inbox = new Inbox(unifiedInbox());
        }
    },

    _setInboxMembersUpdated({ id, count }) {
        if (this._inboxes && this._inboxes[id]) {
            this._inboxes[id].usersCount = count;
        }

        if (this._inboxesSettings && this._inboxesSettings[id]) {
            this._inboxesSettings[id].usersCount = count;
        }
    },

    _setInboxOwnerChanged({ id, count, owner }) {
        if (this._inboxes && this._inboxes[id]) {
            this._inboxes[id].usersCount = count;

            this._inboxes[id].owner.id = owner.id;
            this._inboxes[id].owner.email = owner.email;
            this._inboxes[id].owner.firstName = owner.first_name;
            this._inboxes[id].owner.lastName = owner.last_name;
            this._inboxes[id].owner.fullName = owner.full_name;
            this._inboxes[id].owner.photoUrl = owner.photo_url;
        }

        if (this._inboxesSettings && this._inboxesSettings[id]) {
            this._inboxesSettings[id].usersCount = count;

            this._inboxesSettings[id].owner.id = owner.id;
            this._inboxesSettings[id].owner.email = owner.email;
            this._inboxesSettings[id].owner.firstName = owner.first_name;
            this._inboxesSettings[id].owner.lastName = owner.last_name;
            this._inboxesSettings[id].owner.fullName = owner.full_name;
            this._inboxesSettings[id].owner.photoUrl = owner.photo_url;
        }
    },

    _setInboxDefaultAssigneeSet({ inboxId, userId }) {
        if (this._inboxes && this._inboxes[inboxId]) {
            this._inboxes[inboxId].defaultAssignedUserId = userId;
        }

        if (this._inboxesSettings && this._inboxesSettings[inboxId]) {
            this._inboxesSettings[inboxId].defaultAssignedUserId = userId;
        }
    },

    _setInboxDefaultAssigneeSetError({ inboxId, oldUserId }) {
        if (this._inboxes && this._inboxes[inboxId]) {
            this._inboxes[inboxId].defaultAssignedUserId = oldUserId;
        }

        if (this._inboxesSettings && this._inboxesSettings[inboxId]) {
            this._inboxesSettings[inboxId].defaultAssignedUserId = oldUserId;
        }
    },

    _setInboxRoundRobinUpdated({ inboxId, isRoundRobin }) {
        if (this._inboxes && this._inboxes[inboxId]) {
            this._inboxes[inboxId].isRoundRobin = isRoundRobin;
        }

        if (this._inboxesSettings && this._inboxesSettings[inboxId]) {
            this._inboxesSettings[inboxId].isRoundRobin = isRoundRobin;
        }
    },

    _setInboxHubspotAssignmentUpdated({ inboxId, isHubspotAssignment }) {
        if (this._inboxes && this._inboxes[inboxId]) {
            this._inboxes[inboxId].isHubspotAssignment = isHubspotAssignment;
        }

        if (this._inboxesSettings && this._inboxesSettings[inboxId]) {
            this._inboxesSettings[inboxId].isHubspotAssignment = isHubspotAssignment;
        }
    },

    _setColorCodingLoaded() {
        this._colorCodingLoading = false;
    },

    _setColorCodingLoading() {
        this._colorCodingLoading = true;
    },

    _setConversationsHistoryLoading(status) {
        this._conversationHistoryLoading = status;
    },

    _setReplyAutomationLoading(status) {
        this._replyAutomationLoading = status;
    },
};

export default {
    state,
    getters,
    actions,
};
